import React, { useRef, useState, useEffect, createContext, useContext, useMemo } from "react";
import { useWindowSize } from "react-use";

interface DetailsContextType {
  peers: Element[] | null;
  activeItem: number | null;
  setActive: (index: number) => void;
}

const DetailsContext = createContext<DetailsContextType | undefined>(undefined);
const DetailsItemContext = createContext<{ isActive: boolean } | undefined>(undefined);

interface RootProps {
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
}

function Root({ children, className, as: Component = "div", ...props }: RootProps) {
  const container = useRef<HTMLDivElement | null>(null);

  const [activeItem, setActiveItem] = useState<number | null>(null);

  function setActive(index: number) {
    if (activeItem === index) {
      setActiveItem(null);
    } else {
      setActiveItem(index);
    }
  }

  const [childElements, setChildElements] = useState<Element[]>([]);

  useEffect(() => {
    if (container.current) {
      setChildElements(Array.from(container.current.children));
    }
  }, []);

  const context: DetailsContextType = {
    peers: childElements,
    activeItem,
    setActive,
  };

  return (
    <DetailsContext.Provider value={context}>
      <Component ref={container} className={className} {...props}>
        {children}
      </Component>
    </DetailsContext.Provider>
  );
}

interface ItemProps {
  children: ({ isActive, toggle }: { isActive: boolean; toggle: () => void }) => React.ReactNode;
  className?: string;
  as?: React.ElementType;
}

function Item({ children, className, as: Component = "div", ...props }: ItemProps) {
  const context = useContext(DetailsContext);

  const container = useRef<HTMLDivElement | null>(null);

  const index = useMemo(() => {
    return context?.peers ? context.peers.indexOf(container.current as Element) : -1;
  }, [context?.peers]);

  const isActive = useMemo(() => index === context?.activeItem, [context?.activeItem, index]);

  const toggle = () => context?.setActive(index);

  return (
    <DetailsItemContext.Provider value={{ isActive }}>
      <Component ref={container} className={className} {...props}>
        {children({ isActive, toggle })}
      </Component>
    </DetailsItemContext.Provider>
  );
}

interface ContentProps {
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
}

function Content({ children, className, as: Component = "div", ...props }: ContentProps) {
  const context = useContext(DetailsItemContext);

  const container = useRef<HTMLDivElement | null>(null);

  const [targetHeight, setTargetHeight] = useState<number>(0);

  const windowSize = useWindowSize();

  useEffect(() => {
    setTargetHeight(container.current?.children[0].getBoundingClientRect().height || 0);
  }, [windowSize]);

  const height = useMemo(() => (context?.isActive ? targetHeight : 0), [context?.isActive, targetHeight]);

  return (
    <Component ref={container} style={{ height: `${height}px` }} className={className} {...props}>
      {children}
    </Component>
  );
}

export const Details = Object.assign(Root, { Item, Content });
